import type { SuretyBondFormType } from "@inrev/common";
import type { FC } from "react";
import { bondFormTypeLabelMap } from "../../../../constants";
import { FileRow } from "../../../patterns/file-row";
import { Spinner } from "../../../primitives";
import { FileUploadItem, PendingFileUploadItem } from "../../../ui/FileUploadItem";
import type { BondFormUploadFile } from "./useBondFormUpload";

export type BondFormFileListProps = {
	files: BondFormUploadFile[];
	filesPendingUpload: BondFormUploadFile[];
	fileIdsPendingDelete: string[];
	uploadType: SuretyBondFormType | "";
	compact?: boolean;
	allowDelete?: boolean;
	onDelete: (fileId: string) => void;
	onDownloadFile?: (fileId: string) => void;
	downloadUrl?: string;
};

export const BondFormFileList: FC<BondFormFileListProps> = ({
	files,
	filesPendingUpload,
	fileIdsPendingDelete,
	uploadType,
	compact = false,
	allowDelete = true,
	onDelete,
	onDownloadFile,
	downloadUrl,
}) => {
	if (files.length === 0 && filesPendingUpload.length === 0) return null;

	const types: SuretyBondFormType[] = uploadType === "" ? [] : [uploadType];

	if (compact) {
		return (
			<div className="flex flex-col gap-2" data-testid="bond-form-file-list">
				{files.map((file) => {
					const deleting = fileIdsPendingDelete.includes(file.id);
					return (
						<FileRow
							key={`uploaded:${file.id}`}
							name={`${file.name}.${file.extension}`}
							dataStatus={deleting ? "deleting" : "uploaded"}
							testId="bond-form-file-row"
							removeAriaLabel="Remove bond form"
							status={deleting ? <Spinner size="sm" /> : undefined}
							onRemove={allowDelete ? () => onDelete(file.id) : undefined}
						/>
					);
				})}
				{filesPendingUpload.map((file) => (
					<FileRow
						key={`pending:${file.id}`}
						name={`${file.name}.${file.extension}`}
						dataStatus="uploading"
						muted
						testId="bond-form-file-row"
						status={<Spinner size="sm" />}
					/>
				))}
			</div>
		);
	}

	return (
		<div
			className="w-full max-w-full flex flex-col space-y-[10px] cursor-default"
			data-testid="bond-form-file-list"
		>
			{files.map((file) => (
				<FileUploadItem
					key={`uploaded:${file.id}`}
					{...file}
					types={types}
					enablePDFViewer={true}
					allowDelete={allowDelete}
					typesConfirmedByAdmin={false}
					typeLabelMap={bondFormTypeLabelMap}
					onDelete={onDelete}
					enableTypeSelection={false}
					onDownload={() => onDownloadFile?.(file.id)}
					deleting={fileIdsPendingDelete.includes(file.id)}
					baseUrl={downloadUrl}
					queryKey={"bondFormFile"}
					analyticsContext="submission-upload"
				/>
			))}
			{filesPendingUpload.map((file) => (
				<PendingFileUploadItem
					key={`pending:${file.id}`}
					{...file}
					types={types}
					typeLabelMap={bondFormTypeLabelMap}
				/>
			))}
		</div>
	);
};
